import React from 'react';
import { INPUT_STUDENT } from '../../config/designTokens';
interface SelectOption {
  value: string;
  label: string;
}
interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Label text displayed above the select */
  label?: string;
  /** Error message displayed below the select */
  error?: string;
  /** Helper text displayed below the select (hidden when error is shown) */
  helperText?: string;
  /** Array of options to render */
  options: SelectOption[];
  /** Placeholder option text (empty value) */
  placeholder?: string;
  /** Visual variant: admin (compact) or student (larger, more playful) */
  variant?: 'admin' | 'student';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Optional icon element rendered inside the select (left side) */
  icon?: React.ReactNode;
  /** Full width */
  fullWidth?: boolean;
}
export function Select({
  label,
  error,
  helperText,
  options,
  placeholder,
  variant = 'admin',
  size = 'md',
  icon,
  fullWidth = true,
  className = '',
  id,
  disabled,
  ...props
}: SelectProps) {
  const selectId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined);
  const baseStyles = 'transition-all outline-none bg-white appearance-none';
  const variants = {
    admin: `border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 ${error ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}`,
    student: `${INPUT_STUDENT.base} ${error ? INPUT_STUDENT.error : `${INPUT_STUDENT.focus}`}`
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-sm pr-8',
    md: 'p-2 text-sm pr-8',
    lg: 'px-4 py-3 text-base pr-10'
  };
  const studentSizes = {
    sm: 'px-3 py-2 text-sm pr-8',
    md: 'px-4 py-3 text-base pr-10',
    lg: 'px-5 py-4 text-lg pr-12'
  };
  const sizeClass = variant === 'student' ? studentSizes[size] : sizes[size];
  const labelStyles = {
    admin: 'block text-sm font-medium text-gray-700 mb-1',
    student: 'block text-sm font-bold text-slate-700 mb-2'
  };
  return <div className={fullWidth ? 'w-full' : ''} data-id="element-2519">
      {label && <label htmlFor={selectId} className={labelStyles[variant]} data-id="element-2520">
          {label}
        </label>}
      <div className="relative" data-id="element-2521">
        {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10" data-id="element-2522">
            {icon}
          </span>}
        <select id={selectId} disabled={disabled} className={`
            ${baseStyles}
            ${variants[variant]}
            ${sizeClass}
            ${icon ? 'pl-10' : ''}
            ${fullWidth ? 'w-full' : ''}
            ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}
            ${className}
          `} aria-invalid={error ? 'true' : undefined} aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined} {...props} data-id="element-2523">
          {placeholder && <option value="" data-id="element-2524">{placeholder}</option>}
          {options.map(opt => <option key={opt.value} value={opt.value} data-id="element-2525">
              {opt.label}
            </option>)}
        </select>
        {/* Chevron indicator */}
        <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true" data-id="element-2526">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" data-id="element-2527" />
        </svg>
      </div>
      {error && <p id={`${selectId}-error`} className="mt-1 text-sm text-red-600" role="alert" data-id="element-2528">
          {error}
        </p>}
      {helperText && !error && <p id={`${selectId}-helper`} className="mt-1 text-xs text-gray-500" data-id="element-2529">
          {helperText}
        </p>}
    </div>;
}